// src/App.tsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { BrowserRouter as Router, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { WebAppProvider } from '@vkruglikov/react-telegram-web-app';
import { ThemeProvider } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

import { GlobalStyles } from './styles/GlobalStyles';
import { theme } from './styles/theme';
import { AppRoutes } from './pages/AppRoutes';
import { Layout } from './components/layout/Layout';
import { useTelegramWebApp } from './hooks/useTelegramWebApp';
import { useAuthStore } from './store/authStore';
import { useRetinolStore } from './store/retinolStore';
import { ApiError } from './services/api';
import { AchievementToast } from './components/ui/AchievementToast';
import type { Achievement } from './data/achievementsData';

// ✅ PROD: Отключаем избыточное логирование
if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'production') {
  console.log = () => {};
  console.info = () => {};
  console.warn = () => {};
  console.debug = () => {};
  // console.error оставляем для критичных ошибок
}

// ✅ Создаем клиент для React Query с улучшенными настройками
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 минут
      gcTime: 10 * 60 * 1000, // 10 минут
      retry: (failureCount, error: unknown) => {
        // Не повторяем запросы при 401
        if (error instanceof ApiError && error.status === 401) return false;
        // Максимум 2 попытки для других ошибок
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Экспоненциальная задержка
    },
  },
});

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <WebAppProvider options={{ smoothButtonsTransition: true }}>
        <ThemeProvider theme={theme}>
          <GlobalStyles />
          <Router>
            <AppContent />
          </Router>
        </ThemeProvider>
      </WebAppProvider>
    </QueryClientProvider>
  );
};

// Основной контент приложения
const AppContent: React.FC = () => {
  // ⬇️ из хука теперь также получаем признак реального Telegram
  const { webApp, user, isReady, isRealTelegram } = useTelegramWebApp();
  const { initializeTelegramAuth, checkAuth, refetchUserData, isAuthenticated, isLoading } = useAuthStore();
  const navigate = useNavigate();

  // ✅ Refs для защиты от повторной инициализации (в т.ч. React.StrictMode)
  const bootstrappedRef = useRef(false);
  const lastInitDataRef = useRef<string | null>(null);

  // 1) Один раз: восстановить локальную авторизацию
  useEffect(() => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;

    if (process.env.NODE_ENV === 'development') {
      console.log('⏳ Ждем готовности WebApp...');
    }
    checkAuth();
  },[checkAuth]);

  // 2) Авторизация через Telegram initData — только при реальных условиях
  useEffect(() => {
    if (!isReady || !webApp) return;
    if (isAuthenticated || isLoading) return;

    const initData = webApp.initData || '';
    const looksLikeQuery = typeof initData === 'string' && initData.includes('hash=');

    // Авторизуемся ТОЛЬКО если это реальный Telegram и initData действительно содержит hash
    if (isRealTelegram && looksLikeQuery && initData !== lastInitDataRef.current) {
      lastInitDataRef.current = initData;
      if (process.env.NODE_ENV === 'development') {
        console.log('📱 Найдены валидные данные Telegram, начинаем авторизацию...');
      }
      initializeTelegramAuth(initData, user);
    } else if (process.env.NODE_ENV === 'development') {
      console.log('🔧 Нет валидных данных Telegram — используем только восстановленную авторизацию/DEV');
    }

    // Доп. настройка MainButton (не связана с авторизацией)
    try {
      if (webApp.MainButton && typeof (webApp.MainButton as any).setParams === 'function') {
        (webApp.MainButton as any).setParams({
          text: 'Записаться',
          color: '#7B68EE',
          text_color: '#FFFFFF',
        });
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('✅ WebApp дополнительно настроен:', {
          platform: webApp.platform,
          version: webApp.version,
        });
      }
    } catch (error) {
      console.warn('⚠️ Ошибка дополнительной настройки WebApp:', error);
    }
  },[isReady, webApp, user, isAuthenticated, isLoading, isRealTelegram, initializeTelegramAuth]);

  // Логирование смены авторизации (по желанию)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      if (isAuthenticated) {
        console.log('✅ Пользователь авторизован');
      } else {
        console.log('❌ Пользователь не авторизован');
      }
    }
  },[isAuthenticated]);

  // ✅ Рефетч данных пользователя при первой инициализации после авторизации
  const initialRefetchDoneRef = useRef(false);
  useEffect(() => {
    // Сброс флага при разлогине для повторного рефетча при следующей авторизации
    if (!isAuthenticated) {
      initialRefetchDoneRef.current = false;
      return;
    }

    if (initialRefetchDoneRef.current) return;

    initialRefetchDoneRef.current = true;

    const performInitialRefetch = async () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 Начальный рефетч данных пользователя...');
      }
      const result = await refetchUserData();

      // Если анкета была заполнена на другом устройстве - редирект на календарь
      if (result.questionnaireChanged && result.newValue === true) {
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Анкета заполнена на другом устройстве, редирект на /calendar');
        }
        navigate('/calendar', { replace: true });
      }
      // Если анкета была сброшена - редирект на welcome (обработано ниже в visibilitychange)
    };

    performInitialRefetch();
  }, [isAuthenticated, refetchUserData, navigate]);

  // ✅ Рефетч при возврате в приложение (focus/visibilitychange)
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        if (process.env.NODE_ENV === 'development') {
          console.log('👁️ Приложение стало видимым, рефетч данных пользователя...');
        }
        const result = await refetchUserData();

        // Если анкета была сброшена на другом устройстве - редирект на welcome
        if (result.questionnaireChanged && result.newValue === false) {
          if (process.env.NODE_ENV === 'development') {
            console.log('⚠️ Анкета сброшена на другом устройстве, редирект на /welcome');
          }
          navigate('/welcome', { replace: true });
        }
      }
    };

    const handleFocus = async () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 Окно получило фокус, рефетч данных пользователя...');
      }
      const result = await refetchUserData();

      // Если анкета была сброшена на другом устройстве - редирект на welcome
      if (result.questionnaireChanged && result.newValue === false) {
        if (process.env.NODE_ENV === 'development') {
          console.log('⚠️ Анкета сброшена на другом устройстве, редирект на /welcome');
        }
        navigate('/welcome', { replace: true });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  },[isAuthenticated, refetchUserData, navigate]);

  // ✅ ВАРИАНТ В: Глобальный показ тостов достижений на всех страницах
  const { newAchievements, removeShownAchievement } = useRetinolStore((state) => ({
    newAchievements: state.newAchievements,
    removeShownAchievement: state.removeShownAchievement,
  }));

  const [currentAchievement, setCurrentAchievement] = useState<Achievement | null>(null);

  // Показываем тосты по очереди
  useEffect(() => {
    if (!currentAchievement && newAchievements.length > 0) {
      // Берем первое достижение из очереди
      setCurrentAchievement(newAchievements[0]);
    }
  }, [newAchievements, currentAchievement]);

  const handleCloseAchievement = useCallback(() => {
    if (!currentAchievement) return;

    // Удаляем показанное достижение из очереди
    removeShownAchievement(currentAchievement.id);
    setCurrentAchievement(null);

    // Следующее достижение покажется автоматически через useEffect выше
  }, [currentAchievement, removeShownAchievement]);

  return (
    <AnimatePresence mode="wait">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Layout>
          <AppRoutes />
        </Layout>
      </motion.div>

      {/* ✅ Глобальные тосты достижений - показываются на всех страницах */}
      <AnimatePresence>
        {currentAchievement && (
          <AchievementToast achievement={currentAchievement} onClose={handleCloseAchievement} />
        )}
      </AnimatePresence>
    </AnimatePresence>
  );
};

export default App;
